// Catálogo reutilizable de tarifas de recepción Dropoff -- solapa dentro
// de Sistema → Listas de precios (ver informe "LYNX — Multi-Warehouse,
// Dropoffs, Pickers y Estanterías", ajuste post-PR6: "Catálogo de
// tarifas"). Deliberadamente SIN ningún listado de Warehouses acá: la
// asignación de qué tarifa usa cada Warehouse Dropoff vive en su propio
// modal de edición (WarehousesPage.tsx), no en esta pantalla -- acá solo
// se administra el catálogo en sí (crear, editar, borrar tarifas
// nombradas), reutilizando los mismos componentes visuales (Card/Button/
// Input) que el resto de PriceLists.tsx, nunca su lógica de tariffs.
//
// El backend exige finance_access en TODO el router (ver dropoffTariff.js)
// -- este panel nunca es la autorización real, el 403 del backend lo es.
import React, { useCallback, useEffect, useState } from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { useAuth } from "../../context/AuthContext";
import { authFetch } from "../../lib/authFetch";

type CatalogEntry = { id: number; name: string; amount: string; updated_by: string | null; updated_at: string; warehouses_count: number };

const panelStyle: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "18px", background: "rgba(255,255,255,0.03)", padding: "14px" };
const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-secondary)", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em" };
const tdStyle: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-primary)", verticalAlign: "middle" };

export default function DropoffTariffPanel() {
  const { token } = useAuth();

  const req = useCallback(async (path: string, init: { method?: string; body?: unknown } = {}) => {
    const response = await authFetch(path, { token, method: init.method ?? "GET", json: init.body !== undefined, body: init.body === undefined ? undefined : JSON.stringify(init.body) });
    const json = await response.json().catch(() => ({}));
    if (!response.ok || json?.ok === false) throw new Error(json?.error || `Error ${response.status}`);
    return json;
  }, [token]);

  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [newName, setNewName] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [creating, setCreating] = useState(false);

  // name/amount en edición por fila, indexado por id -- misma idea que
  // rowInputs del panel anterior, ahora sobre el catálogo en vez de sobre
  // Warehouses.
  const [rowEdits, setRowEdits] = useState<Record<number, { name: string; amount: string }>>({});
  const [saving, setSaving] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const json = await req("/v2/dropoff-tariff/catalog");
      const list: CatalogEntry[] = json.catalog ?? [];
      setCatalog(list);
      const edits: Record<number, { name: string; amount: string }> = {};
      for (const c of list) edits[c.id] = { name: c.name, amount: String(c.amount) };
      setRowEdits(edits);
    } catch (e: any) {
      setError(e.message || "No se pudo cargar el catálogo de tarifas");
    } finally { setLoading(false); }
  }, [req]);

  useEffect(() => { load(); }, [load]);

  const flash = (msg: string) => { setNotice(msg); window.setTimeout(() => setNotice(null), 6000); };

  const createEntry = async () => {
    const amount = Number(newAmount);
    if (!newName.trim()) { setError("Falta el nombre de la tarifa"); return; }
    if (!Number.isFinite(amount) || amount < 0) { setError("El importe tiene que ser un número mayor o igual a 0"); return; }
    setCreating(true); setError(null);
    try {
      await req("/v2/dropoff-tariff/catalog", { method: "POST", body: { name: newName.trim(), amount } });
      flash(`Tarifa «${newName.trim()}» creada`);
      setNewName(""); setNewAmount("");
      await load();
    } catch (e: any) { setError(e.message); } finally { setCreating(false); }
  };

  const saveEntry = async (entryId: number) => {
    const edit = rowEdits[entryId];
    const amount = Number(edit?.amount ?? "");
    if (!edit?.name.trim()) { setError("Falta el nombre de la tarifa"); return; }
    if (!Number.isFinite(amount) || amount < 0) { setError("El importe tiene que ser un número mayor o igual a 0"); return; }
    setSaving(entryId); setError(null);
    try {
      await req(`/v2/dropoff-tariff/catalog/${entryId}`, { method: "PUT", body: { name: edit.name.trim(), amount } });
      flash("Tarifa guardada");
      await load();
    } catch (e: any) { setError(e.message); } finally { setSaving(null); }
  };

  const deleteEntry = async (entry: CatalogEntry) => {
    if (!window.confirm(`¿Eliminar la tarifa «${entry.name}»?`)) return;
    setSaving(entry.id); setError(null);
    try {
      await req(`/v2/dropoff-tariff/catalog/${entry.id}`, { method: "DELETE" });
      flash(`Tarifa «${entry.name}» eliminada`);
      await load();
    } catch (e: any) { setError(e.message); } finally { setSaving(null); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <h2 style={{ margin: "0 0 6px 0", fontSize: "20px", fontWeight: 700, color: "var(--text-primary)" }}>Catálogo de tarifas Dropoff</h2>
        <p style={{ ...smallMutedStyle, margin: "0 0 16px 0" }}>
          Tarifas nombradas y reutilizables por cualquier cantidad de Warehouses. La asignación de cuál usa cada Dropoff se hace desde
          su propio modal de edición en Sistema → Warehouses, no acá. Un Crossdock nunca genera esta tarifa. Cada recepción congela el
          importe vigente en ese momento: cambiar una tarifa acá nunca modifica recepciones ya registradas.
        </p>

        {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)", marginBottom: "14px" }}>{error} <Button variant="ghost" onClick={() => setError(null)}>Cerrar</Button></div> : null}
        {notice ? <div style={{ ...panelStyle, borderColor: "var(--success)", color: "var(--success)", marginBottom: "14px" }}>{notice}</div> : null}

        <div style={panelStyle}>
          <div style={{ ...smallMutedStyle, marginBottom: "8px", fontWeight: 600 }}>Nueva tarifa</div>
          <div style={{ display: "flex", gap: "12px", alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ minWidth: "220px" }}>
              <Input label="Nombre" value={newName} onChange={(e: any) => setNewName(e.target.value)} placeholder="General" />
            </div>
            <div style={{ minWidth: "160px" }}>
              <Input label="Importe ($)" type="number" min={0} step="0.01" value={newAmount} onChange={(e: any) => setNewAmount(e.target.value)} placeholder="0.00" />
            </div>
            <Button onClick={createEntry} disabled={creating}>{creating ? "Creando…" : "Crear tarifa"}</Button>
          </div>
        </div>
      </Card>

      <Card>
        <h3 style={{ margin: "0 0 6px 0", fontSize: "16px", color: "var(--text-primary)" }}>Tarifas existentes</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead><tr><th style={thStyle}>Nombre</th><th style={thStyle}>Importe</th><th style={thStyle}>En uso</th><th style={thStyle}></th></tr></thead>
            <tbody>
              {loading && !catalog.length ? (
                <tr><td style={tdStyle} colSpan={4}>Cargando…</td></tr>
              ) : !catalog.length ? (
                <tr><td style={tdStyle} colSpan={4}>Todavía no hay ninguna tarifa en el catálogo. Creá la primera arriba.</td></tr>
              ) : catalog.map((c) => (
                <tr key={c.id}>
                  <td style={tdStyle}>
                    <Input
                      value={rowEdits[c.id]?.name ?? ""}
                      onChange={(e: any) => setRowEdits((prev) => ({ ...prev, [c.id]: { ...prev[c.id], name: e.target.value } }))}
                    />
                  </td>
                  <td style={tdStyle}>
                    <Input
                      type="number" min={0} step="0.01"
                      value={rowEdits[c.id]?.amount ?? ""}
                      onChange={(e: any) => setRowEdits((prev) => ({ ...prev, [c.id]: { ...prev[c.id], amount: e.target.value } }))}
                    />
                  </td>
                  <td style={tdStyle}>
                    {c.warehouses_count > 0 ? <span>{c.warehouses_count} Warehouse(s)</span> : <span style={smallMutedStyle}>sin usar</span>}
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Button variant="secondary" onClick={() => saveEntry(c.id)} disabled={saving === c.id}>
                        {saving === c.id ? "Guardando…" : "Guardar"}
                      </Button>
                      <Button variant="ghost" onClick={() => deleteEntry(c)} disabled={saving === c.id}>Eliminar</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
